import React, { useState, useEffect } from "react";
import { Spinner, Form, Row, Col, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import "../../Styles/yellocalfcss/SelectStudent.css";
import ApiConfig from "../../api/ApiConfig";
import { simplePostCall } from "../../api/ApiServices";
import { toast, ToastContainer } from "react-toastify";
import ScaleLoader from "react-spinners/ScaleLoader";

const GenerateArrearInvoice = () => {
  const navigate = useNavigate();

  const [grades, setGrades] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [terms, setTerms] = useState([]);
  const [studentList, setStudentList] = useState([]);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [studentValues, setStudentValues] = useState({});

  // --- Validation State ---
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    grade: "",
    academicYear: "",
    term: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [isTableLoading, setIsTableLoading] = useState(false);

  useEffect(() => {
    const loadInitialData = async () => {
      setIsFetching(true);
      await Promise.all([fetchGrades(), fetchAcademicYears(), fetchTerms()]);
      setIsFetching(false);
    };
    loadInitialData();
  }, []);

  const fetchGrades = async () => {
    try {
      const res = await simplePostCall(
        ApiConfig.classlist,
        JSON.stringify({
          customer_id: Number(localStorage.getItem("customer_id")),
          api_key: localStorage.getItem("api_key"),
        }),
      );
      if (res?.classread) setGrades(res.classread);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchTerms = async () => {
    try {
      const res = await simplePostCall(
        ApiConfig.termstype,
        JSON.stringify({
          customer_id: Number(localStorage.getItem("customer_id")),
          api_key: localStorage.getItem("api_key"),
        }),
      );
      if (res?.master_academic) setTerms(res.master_academic);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAcademicYears = async () => {
    try {
      const res = await simplePostCall(
        ApiConfig.academicyear,
        JSON.stringify({
          customer_id: Number(localStorage.getItem("customer_id")),
          api_key: localStorage.getItem("api_key"),
          language: "en",
        }),
      );
      if (res?.academicYear) setAcademicYears(res.academicYear);
    } catch (err) {
      console.error(err);
    }
  };

  const handleGradeChange = async (e) => {
    const gradeId = e.target.value;
    setFormData({ ...formData, grade: gradeId });
    setStudentList([]);
    setSelectedStudents([]);
    setStudentValues({});

    // Clear grade error when user selects one
    if (errors.grade) {
      setErrors((prev) => ({ ...prev, grade: false }));
    }

    if (gradeId) {
      setIsTableLoading(true);
      try {
        const res = await simplePostCall(
          ApiConfig.Studentclasslist,
          JSON.stringify({
            customer_id: Number(localStorage.getItem("customer_id")),
            api_key: localStorage.getItem("api_key"),
            class_id: gradeId,
            language: "en",
          }),
        );
        if (res?.result && res.academicYear) setStudentList(res.academicYear);
      } catch (err) {
        console.error(err);
      } finally {
        setIsTableLoading(false);
      }
    }
  };

  const handleInputChange = (studentId, field, value) => {
    setStudentValues((prev) => ({
      ...prev,
      [studentId]: { ...prev[studentId], [field]: value },
    }));

    // Clear specific field error as user types
    const errorKey = `${studentId}_${field}`;
    if (errors[errorKey]) {
      const newErrors = { ...errors };
      delete newErrors[errorKey];
      setErrors(newErrors);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedStudents(studentList.map((s) => s.student_id));
    } else {
      setSelectedStudents([]);
      // Clear all student-specific errors when deselecting all
      const newErrors = { ...errors };
      Object.keys(newErrors).forEach((key) => {
        if (key.includes("_")) delete newErrors[key];
      });
      setErrors(newErrors);
    }
  };

  const handleStudentSelect = (id) => {
    setSelectedStudents((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  // --- Validation Logic ---
  const validateForm = () => {
    const newErrors = {};

    // 1. Header Dropdown Validations
    if (!formData.grade) newErrors.grade = true;
    if (!formData.academicYear) newErrors.academicYear = true;
    if (!formData.term) newErrors.term = true;
    if (!selectedIncomeType) newErrors.incomeType = true;

    // 2. Selection Validation
    if (selectedStudents.length === 0) {
      toast.warning("Please select at least one student from the table.");
      setErrors(newErrors);
      return false;
    }

    // 3. Row-level Validations (Discount, Bonus, Amount)
    selectedStudents.forEach((id) => {
      const val = studentValues[id] || {};

      // Amount is Mandatory if student is selected
      if (!val.amount || parseFloat(val.amount) <= 0) {
        newErrors[`${id}_amount`] = "Required > 0";
      }

      // Bonus/Discount shouldn't be negative
      if (val.discount && parseFloat(val.discount) < 0) {
        newErrors[`${id}_discount`] = "No negatives";
      }

      // Bonus: Optional, but cannot be negative
      if (val.bonus && parseFloat(val.bonus) < 0) {
        newErrors[`${id}_bonus`] = "No negatives";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const [selectedIncomeType, setSelectedIncomeType] = useState("");
  const [recordList, setRecordsList] = useState([]);
  /* ---------------- FETCH LIST ---------------- */
  const fetchIncomeTypes = async () => {
    const body = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      user_id: Number(localStorage.getItem("user_id")),
      language: "en",
      page: 1,
      limit: 9999,
      // name_search: nameSearch,
      // account_name_search: accountSearch,
      // date_search: dateSearch,
    };

    try {
      const res = await simplePostCall(
        ApiConfig.incomeTypeList,
        JSON.stringify(body),
      );

      if (res?.result) {
        setRecordsList(res.data || []);
      } else {
        setRecordsList([]);
      }
    } catch {
    } finally {
    }
  };

  useEffect(() => {
    fetchIncomeTypes();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return; // Stop if validation fails
    }

    setIsLoading(true);
    const amounts = {};
    const bonuses = {};
    const discounts = {};

    selectedStudents.forEach((id) => {
      amounts[id] = studentValues[id]?.amount || "0";
      bonuses[id] = studentValues[id]?.bonus || "0";
      discounts[id] = studentValues[id]?.discount || "0";
    });

    const payload = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      language: "en",
      academic: formData.academicYear,
      terms: formData.term,
      students: selectedStudents,
      amount: amounts,
      bonus: bonuses,
      discount: discounts,
      income_type: selectedIncomeType,
    };

    try {
      const res = await simplePostCall(
        ApiConfig.feeinvoiceAdd,
        JSON.stringify(payload),
      );
      if (res?.result) {
        toast.success("Arrear invoices generated successfully!");
        setTimeout(() => {
          navigate("/UpcomingInvoices"); // Redirect to view page
        }, 1200);
      } else {
        toast.error(res?.message || "Something went wrong");
      }
    } catch (err) {
      console.error("Submit failed", err);
      toast.error("An error occurred during submission.");
    } finally {
      setIsLoading(false);
    }
  };

  // if (isFetching) {
  //   return (
  //       <div className="text-center vh-100 d-flex align-items-center justify-content-center">
  //            <ScaleLoader color="#F4BB44" />
  //       </div>
  //   );
  // }

  return (
    <div className="SelectStudent-container my-4">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="container-fluid bg-white shadow-sm py-2 px-4 mb-3 rounded">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center">
          <h4 className="mb-2 mb-md-0 text-dark">Generate Arrear Invoice</h4>
          <button
            style={{ backgroundColor: "#5db2e8", color: "white" }}
            className="btn"
            onClick={() => navigate("/import-invoice")}
          >
            Import Arrears
          </button>
        </div>
      </div>

      <div className="SelectStudent-invoice-form mt-3 bg-white p-4 shadow-sm rounded">
        <Form onSubmit={handleSubmit}>
          <Row className="mb-4">
            <Col md={6} className="mb-3">
              <Form.Label>
                Select Grade <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={formData.grade}
                onChange={handleGradeChange}
                isInvalid={!!errors.grade}
              >
                <option value="">Select Grade</option>
                {grades.map((g) => (
                  <option key={g.class_id} value={g.class_id}>
                    {g.class_name}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                Grade is required.
              </Form.Control.Feedback>
            </Col>

            <Col md={6} className="mb-3">
              <Form.Label>Select Division</Form.Label>

              <Form.Select
                value={formData.division}
                onChange={(e) =>
                  setFormData({ ...formData, division: e.target.value })
                }
              >
                <option value="">Select Division</option>
              </Form.Select>
            </Col>

            <Col md={6} className="mb-3">
              <Form.Label>
                Select Income Type <span className="text-danger">*</span>
              </Form.Label>

              <Form.Select
                value={selectedIncomeType}
                isInvalid={!!errors.incomeType}
                onChange={(e) => {
                  setSelectedIncomeType(e.target.value);

                  // Same behavior like Terms & Academic Year
                  if (errors.incomeType) {
                    setErrors((prev) => ({ ...prev, incomeType: false }));
                  }
                }}
              >
                <option value="">Select Type</option>
                {recordList?.map((record) => (
                  <option
                    key={record.income_type_id}
                    value={record.income_type_id}
                  >
                    {record.income_type_name}
                  </option>
                ))}
              </Form.Select>

              <Form.Control.Feedback type="invalid">
                Income Type is required.
              </Form.Control.Feedback>
            </Col>

            <Col md={6} className="mb-3">
              <Form.Label>
                Select Academic Year <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={formData.academicYear}
                onChange={(e) =>
                  setFormData({ ...formData, academicYear: e.target.value })
                }
                isInvalid={!!errors.academicYear}
              >
                <option value="">Select Academic Year</option>
                {academicYears.map((y) => (
                  <option key={y.academic_year_id} value={y.academic_year_id}>
                    {y.academic_year_name}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                Academic Year is required.
              </Form.Control.Feedback>
            </Col>

            <Col md={6} className="mb-3">
              <Form.Label>
                Select Terms/Semester <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={formData.term}
                isInvalid={!!errors.term}
                onChange={(e) => {
                  setFormData({ ...formData, term: e.target.value });

                  // 👉 Clear error when user selects value
                  if (errors.term) {
                    setErrors((prev) => ({ ...prev, term: false }));
                  }
                }}
              >
                <option value="">Select Terms/Semester</option>
                {terms
                  .filter((t) => t.terms_status === "active")
                  .map((t) => (
                    <option key={t.terms_id} value={t.terms_id}>
                      {t.terms_name}
                    </option>
                  ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                Terms/Semester is required.
              </Form.Control.Feedback>
            </Col>
          </Row>

          <div className="student-table-wrapper mt-4">
            {isTableLoading ? (
              <div className="text-center py-4">
                <Spinner animation="border" variant="primary" />
              </div>
            ) : studentList.length > 0 ? (
              <Table
                responsive
                striped
                bordered
                hover
                className="mt-2 align-middle"
              >
                <thead className="bg-light">
                  <tr>
                    <th style={{ width: "50px" }}>
                      <Form.Check
                        type="checkbox"
                        onChange={handleSelectAll}
                        checked={
                          selectedStudents.length === studentList.length &&
                          studentList.length > 0
                        }
                      />
                    </th>
                    <th>Students</th>
                    <th className="text-center">
                      Amount <span className="text-danger">*</span>
                    </th>
                    <th className="text-center">Discount</th>
                    <th className="text-center">Bonus</th>
                  </tr>
                </thead>
                <tbody>
                  {studentList.map((student) => {
                    const isSelected = selectedStudents.includes(
                      student.student_id,
                    );
                    return (
                      <tr
                        key={student.student_id}
                        className={isSelected ? "table-primary-light" : ""}
                      >
                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={isSelected}
                            onChange={() =>
                              handleStudentSelect(student.student_id)
                            }
                          />
                        </td>
                        <td>
                          <div className="fw-bold">{student.student_name}</div>
                          <small className="text-muted">
                            {student.student_admission_num ||
                              student.student_id}
                          </small>
                        </td>
                        <td className="text-center input-cell">
                          <Form.Control
                            type="number"
                            placeholder="0.00"
                            disabled={!isSelected}
                            isInvalid={!!errors[`${student.student_id}_amount`]}
                            value={
                              studentValues[student.student_id]?.amount || ""
                            }
                            onChange={(e) =>
                              handleInputChange(
                                student.student_id,
                                "amount",
                                e.target.value,
                              )
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            Required
                          </Form.Control.Feedback>
                        </td>
                        <td>
                          <Form.Control
                            type="number"
                            placeholder="0.00"
                            disabled={!isSelected}
                            isInvalid={
                              !!errors[`${student.student_id}_discount`]
                            }
                            value={
                              studentValues[student.student_id]?.discount || ""
                            }
                            onChange={(e) =>
                              handleInputChange(
                                student.student_id,
                                "discount",
                                e.target.value,
                              )
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors[`${student.student_id}_discount`]}
                          </Form.Control.Feedback>
                        </td>
                        <td>
                          <Form.Control
                            type="number"
                            placeholder="0.00"
                            disabled={!isSelected}
                            isInvalid={!!errors[`${student.student_id}_bonus`]}
                            value={
                              studentValues[student.student_id]?.bonus || ""
                            }
                            onChange={(e) =>
                              handleInputChange(
                                student.student_id,
                                "bonus",
                                e.target.value,
                              )
                            }
                          />
                          <Form.Control.Feedback type="invalid">
                            {errors[`${student.student_id}_bonus`]}
                          </Form.Control.Feedback>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            ) : (
              formData.grade &&
              !isTableLoading && (
                <div className="text-center py-3 text-muted">
                  No students found for this grade.
                </div>
              )
            )}
          </div>

          <div className="d-flex justify-content-end mt-4">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading || selectedStudents.length === 0}
            >
              {isLoading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Processing...
                </>
              ) : (
                "Generate Invoices"
              )}
            </button>
          </div>
        </Form>
      </div>
    </div>
  );
};

export default GenerateArrearInvoice;
